import { Project, UserSettings } from "@timeghost/web-api";
import {
	addDays,
	addMinutes,
	addMonths,
	addWeeks,
	addYears,
	endOfDay,
	endOfMonth,
	endOfWeek,
	endOfYear,
	startOfDay,
	startOfMonth,
	startOfWeek,
	startOfYear,
} from "date-fns/esm";
import { isProjectManager } from "./permission";
import userProjectFind from "./userProjectFind";

// returns a date for when comegos should be locked
export function getComegoLockedDate(currentUser: UserSettings, selectedUserId: string) {
	const blockComego = currentUser.workspace.settings.blockComego;
	if (!blockComego) return null;
	const mode: string = blockComego.pastMode;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);

	if (isAdmin) return null;
	if (currentUser.id != selectedUserId) return null;

	if (mode === "none") {
		return null;
	} else if (mode == "date") {
		return new Date(blockComego.pastDate);
	} else {
		const period: number = blockComego.period;
		const range: string = blockComego.range;
		let allowedDate = startOfDay(new Date());
		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, -(period * 60));
				break;
			case "days":
				allowedDate = addDays(allowedDate, -period);
				break;
			case "startDay":
				allowedDate = startOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = startOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "lastWeek":
				allowedDate = startOfWeek(addWeeks(allowedDate, -1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = startOfMonth(allowedDate);
				break;
			case "lastMonth":
				allowedDate = startOfMonth(addMonths(allowedDate, -1));
				break;
			case "thisYear":
				allowedDate = startOfYear(allowedDate);
				break;
			case "lastYear":
				allowedDate = startOfYear(addYears(allowedDate, -1));
				break;
			default:
				return null;
		}
		return allowedDate;
	}
}
// returns true if the given date should be locked, for comegos
export function isComegoLockedForDate(currentUser: UserSettings, date: Date) {
	const blockComego = currentUser.workspace.settings.blockComego;
	if (!blockComego) return false;
	const mode: string = blockComego.pastMode;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
	let allowedDate;
	if (isAdmin) return false;
	if (mode === "none") {
		return false;
	} else if (mode == "date") {
		allowedDate = new Date(blockComego.pastDate);
	} else {
		const period: number = blockComego.period;
		const range: string = blockComego.range;
		allowedDate = startOfDay(new Date());
		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, -(period * 60));
				break;
			case "days":
				allowedDate = addDays(allowedDate, -period);
				break;
			case "startDay":
				allowedDate = startOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = startOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "lastWeek":
				allowedDate = startOfWeek(addWeeks(allowedDate, -1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = startOfMonth(allowedDate);
				break;
			case "lastMonth":
				allowedDate = startOfMonth(addMonths(allowedDate, -1));
				break;
			case "thisYear":
				allowedDate = startOfYear(allowedDate);
				break;
			case "lastYear":
				allowedDate = startOfYear(addYears(allowedDate, -1));
				break;
			default:
				return false;
		}
		//
	}
	return date < allowedDate;
}
// returns a date for when comegos should be locked
export function getComegoFutureLockedDate(currentUser: UserSettings, selectedUserId: string) {
	const blockComego = currentUser.workspace.settings.blockComego;
	if (!blockComego) return null;
	const mode: string = blockComego.futureMode;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);

	if (isAdmin) return null;
	if (currentUser.id != selectedUserId) return null;

	if (mode === "none") {
		return null;
	} else if (mode == "date") {
		return new Date(blockComego.futureDate);
	} else {
		const period: number = blockComego.futurePeriod;
		const range: string = blockComego.futureRange;
		let allowedDate = endOfDay(new Date());
		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, period * 60);
				break;
			case "days":
				allowedDate = addDays(allowedDate, period);
				break;
			case "endDay":
				allowedDate = endOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = endOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "nextWeek":
				allowedDate = endOfWeek(addWeeks(allowedDate, 1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = endOfMonth(allowedDate);
				break;
			case "nextMonth":
				allowedDate = endOfMonth(addMonths(allowedDate, 1));
				break;
			case "thisYear":
				allowedDate = endOfYear(allowedDate);
				break;
			case "nextYear":
				allowedDate = endOfYear(addYears(allowedDate, 1));
				break;
			default:
				return null;
		}
		return allowedDate;
	}
}
// returns true if the given date should be locked, for comegos
export function isComegoLockedForDateFuture(currentUser: UserSettings, date: Date) {
	const blockComego = currentUser.workspace.settings.blockComego;
	if (!blockComego) return false;
	const mode: string = blockComego.futureMode;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
	let allowedDate;
	if (isAdmin) return false;
	if (mode === "none") {
		return false;
	} else if (mode == "date") {
		allowedDate = new Date(blockComego.pastDate);
	} else {
		const period: number = blockComego.futurePeriod;
		const range: string = blockComego.futureRange;
		allowedDate = endOfDay(new Date());
		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, period * 60);
				break;
			case "days":
				allowedDate = addDays(allowedDate, period);
				break;
			case "endDay":
				allowedDate = startOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = endOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "lastWeek":
				allowedDate = endOfWeek(addWeeks(allowedDate, 1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = endOfMonth(allowedDate);
				break;
			case "lastMonth":
				allowedDate = endOfMonth(addMonths(allowedDate, 1));
				break;
			case "thisYear":
				allowedDate = endOfYear(allowedDate);
				break;
			case "lastYear":
				allowedDate = endOfYear(addYears(allowedDate, 1));
				break;
			default:
				return false;
		}
	}
	return date > allowedDate;
}

// returns a date for when times should be locked
export function getTimesLockedDate(currentUser: UserSettings, project: any) {
	const blockTimes = currentUser.workspace.settings.blockTimes;
	if (!blockTimes) return null;
	const mode: string = blockTimes.pastMode;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
	const isManager = userProjectFind(currentUser, project, { projectManager: true, workspaceAdmin: true });
	//const isManager = false;

	if (isAdmin) return null;
	if (isManager) return null;

	if (mode === "none") {
		return null;
	} else if (mode == "date") {
		return new Date(blockTimes.pastDate);
	} else {
		const period: number = blockTimes.period;
		const range: string = blockTimes.range;
		let allowedDate = startOfDay(new Date());

		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, -(period * 60));
				break;
			case "days":
				allowedDate = addDays(allowedDate, -period);
				break;
			case "startDay":
				allowedDate = startOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = startOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "lastWeek":
				allowedDate = startOfWeek(addWeeks(allowedDate, -1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = startOfMonth(allowedDate);
				break;
			case "lastMonth":
				allowedDate = startOfMonth(addMonths(allowedDate, -1));
				break;
			case "thisYear":
				allowedDate = startOfYear(allowedDate);
				break;
			case "lastYear":
				allowedDate = startOfYear(addYears(allowedDate, -1));
				break;
			default:
				return null;
		}
		return allowedDate;
	}
}
// returns true if the given date should be locked, times
export function isTimeLockedForDate(currentUser: UserSettings, project: Project | null | undefined, date: Date) {
	const blockTimes = currentUser.workspace.settings.blockTimes;
	if (!blockTimes) return false;
	const mode: string = blockTimes.pastMode;
	if (!mode) return false;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
	const isManager = project && isProjectManager(currentUser.id, currentUser.workspace, project);
	//const isManager = false;
	let allowedDate = startOfDay(new Date());
	if (isAdmin) return false;
	if (isManager) return false;

	if (!mode || mode === "none") {
		return false;
	} else if (mode == "date") {
		allowedDate = new Date(blockTimes.pastDate);
	} else {
		const period: number = blockTimes.period;
		const range: string = blockTimes.range;
		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, -(period * 60));
				break;
			case "days":
				allowedDate = addDays(allowedDate, -period);
				break;
			case "startDay":
				allowedDate = startOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = startOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "lastWeek":
				allowedDate = startOfWeek(addWeeks(allowedDate, -1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = startOfMonth(allowedDate);
				break;
			case "lastMonth":
				allowedDate = startOfMonth(addMonths(allowedDate, -1));
				break;
			case "thisYear":
				allowedDate = startOfYear(allowedDate);
				break;
			case "lastYear":
				allowedDate = startOfYear(addYears(allowedDate, -1));
				break;
			default:
				return false;
		}
	}
	return date < allowedDate;
}
// returns a date for when times should be locked
export function getTimesFutureLockDate(currentUser: UserSettings, project: any) {
	const blockTimes = currentUser.workspace.settings.blockTimes;
	if (!blockTimes) return null;
	const mode: string = blockTimes.futureMode;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
	const isManager = userProjectFind(currentUser, project, { projectManager: true, workspaceAdmin: true });

	if (isAdmin) return null;
	if (isManager) return null;

	if (mode === "none") {
		return null;
	} else if (mode == "date") {
		return new Date(blockTimes.futureDate);
	} else {
		const period: number = blockTimes.futurePeriod;
		const range: string = blockTimes.futureRange;
		let allowedDate = endOfDay(new Date());

		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, period * 60);
				break;
			case "days":
				allowedDate = addDays(allowedDate, period);
				break;
			case "endDay":
				allowedDate = endOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = endOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "nextWeek":
				allowedDate = endOfWeek(addWeeks(allowedDate, 1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = endOfMonth(allowedDate);
				break;
			case "nextMonth":
				allowedDate = endOfMonth(addMonths(allowedDate, 1));
				break;
			case "thisYear":
				allowedDate = endOfYear(allowedDate);
				break;
			case "nextYear":
				allowedDate = endOfYear(addYears(allowedDate, 1));
				break;
			default:
				return null;
		}
		return allowedDate;
	}
}
// returns true if the given date should be locked, times
export function isTimeLockedForDateFuture(currentUser: UserSettings, project: Project | null | undefined, date: Date) {
	const blockTimes = currentUser.workspace.settings.blockTimes;
	if (!blockTimes) return false;
	const mode: string = blockTimes.futureMode;
	if (!mode) return false;
	const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
	const isManager = project && isProjectManager(currentUser.id, currentUser.workspace, project);
	//const isManager = false;
	let allowedDate = endOfDay(new Date());
	if (isAdmin) return false;
	if (isManager) return false;

	if (!mode || mode === "none") {
		return false;
	} else if (mode == "date") {
		allowedDate = new Date(blockTimes.futureDate);
	} else {
		const period: number = blockTimes.futurePeriod;
		const range: string = blockTimes.futureRange;
		switch (range) {
			case "hours":
				allowedDate = new Date();
				allowedDate = addMinutes(allowedDate, period * 60);
				break;
			case "days":
				allowedDate = addDays(allowedDate, period);
				break;
			case "endDay":
				allowedDate = endOfDay(allowedDate);
				break;
			case "thisWeek":
				allowedDate = endOfWeek(allowedDate, { weekStartsOn: 1 });
				break;
			case "nextWeek":
				allowedDate = endOfWeek(addWeeks(allowedDate, 1), { weekStartsOn: 1 });
				break;
			case "thisMonth":
				allowedDate = endOfMonth(allowedDate);
				break;
			case "nextMonth":
				allowedDate = endOfMonth(addMonths(allowedDate, 1));
				break;
			case "thisYear":
				allowedDate = endOfYear(allowedDate);
				break;
			case "nextYear":
				allowedDate = endOfYear(addYears(allowedDate, 1));
				break;
			default:
				return false;
		}
	}
	return date > allowedDate;
}
